<template>
    <CContainer :class="{'in-page': inline}" class="dropship-entry content-center d-flex min-vh-100">
        <CRow>
            <CCol>
                <CCardGroup>
                    <CCard class="p-8">
                        <CCardHeader>
                            <img
                                src="../../../public/logo_1.png"
                                alt=""
                                class="login-logo"
                            />
                            <h2>Enter Drop-Ship Address</h2>
                        </CCardHeader>
                        <CCardBody>

                            <CForm @submit="doSubmit">

                                <CRow>
                                    <CCol col="7">
                                        <label class="text-muted">Requested Ship Date</label>
                                    </CCol>
                                </CRow>
                                <CRow class="mb-4">
                                    <CCol col="12">
                                        <ShipDatePicker @date-selected="setShipDate" :shipDate="estimatedShipDate" />
                                    </CCol>
                                </CRow>

                                <CRow v-if="!selectedCountry">
                                    <CCol class="text-left">
                                        <label class="text-muted">Country</label>
                                        <div class="d-flex align-items-center">
                                            <CSelect
                                                class="w-100"
                                                placeholder="Please select a country"
                                                :value.sync="selectedCountry"
                                                :options="countryOptions"
                                                :disabled="countrySelectDisabled"
                                                @change="handleCountryChange"
                                            />
                                            <CSpinner class="ml-2 mb-2" v-if="useSpinners && countrySelectDisabled" color="info" size="sm" />
                                        </div>
                                    </CCol>
                                </CRow>

                                <div v-if="selectedCountry">
                                    <CRow>
                                        <CCol>
                                            <label class="text-muted">Name</label>
                                            <CInput
                                                ref="dropshipName"
                                                v-model="name"
                                                :is-valid="isLengthValid(name, MAX_LENGTHS.name)"
                                                required
                                            >
                                            </CInput>
                                        </CCol>
                                    </CRow>

                                    <CRow>
                                        <CCol>
                                            <label class="text-muted">Address</label>
                                            <CInput
                                                v-model="addressLine1"
                                                placeholder="Line 1"
                                                :is-valid="isLengthValid(addressLine1, MAX_LENGTHS.addressLine1) || isLengthValid(addressLine2, MAX_LENGTHS.addressLine2)"
                                                :required="!isLengthValid(addressLine1, MAX_LENGTHS.addressLine1) && !isLengthValid(addressLine2, MAX_LENGTHS.addressLine2)"
                                            >
                                            </CInput>
                                            <CInput
                                                v-model="addressLine2"
                                                placeholder="Line 2"
                                                :is-valid="addressLine2 === '' || isLengthValid(addressLine2, MAX_LENGTHS.addressLine2) || isLengthValid(addressLine1, MAX_LENGTHS.addressLine1)"
                                            >
                                            </CInput>
                                        </CCol>
                                    </CRow>

                                    <CRow>
                                        <CCol class="text-left">
                                            <label class="text-muted">City</label>
                                            <CInput
                                                v-model="city"
                                                placeholder=""
                                                :is-valid="isLengthValid(city, MAX_LENGTHS.city)"
                                                required
                                            >
                                            </CInput>
                                        </CCol>
                                        <CCol class="text-left">
                                            <label class="text-muted">Postal</label>
                                            <CInput
                                                v-model="postal"
                                                placeholder=""
                                                :is-valid="isLengthValid(postal, MAX_LENGTHS.postal)"
                                                required
                                            >
                                            </CInput>
                                        </CCol>
                                    </CRow>

                                    <CRow>
                                        <CCol class="text-left">
                                            <label class="text-muted">{{ labelStateOrProvince }}  </label>
                                            <CSelect
                                                :value.sync="selectedState"
                                                :options="stateOptions"
                                                :placeholder="'Select a ' + labelStateOrProvince"
                                                :invalid-feedback="'Required'"
                                                :is-valid="isStateValid"
                                                :disabled="!stateOptions || !stateOptions.length"
                                                required
                                            />
                                            <CSpinner class="ml-2 mb-2" v-if="!stateOptions || !stateOptions.length" color="info" size="sm" />
                                        </CCol>
                                        <CCol class="text-left">
                                            <label class="text-muted">Country</label>
                                            <CSelect
                                                placeholder="Select a country"
                                                :value.sync="selectedCountry"
                                                :options="countryOptions"
                                                :invalid-feedback="'Required'"
                                                @change="handleCountryChange"
                                                required
                                            />
                                        </CCol>
                                    </CRow>
                                </div>

                                <CRow>
                                    <CCol col="12" class="text-left">
                                        <div class="float-left">
                                            <CButton
                                                v-if="customerHasWarehouses"
                                                color="secondary"
                                                class="px-4"
                                                @click.prevent="goToWarehouseSelection"
                                            >
                                                <CIcon name="cilChevronLeft" /> Warehouse
                                            </CButton>
                                            <CButton
                                                v-else
                                                color="secondary"
                                                class="px-4"
                                                @click.prevent="goToCustomerSelection"
                                            >
                                                <CIcon name="cilChevronLeft" /> Customer
                                            </CButton>
                                        </div>
                                        <div class="float-right">
                                            <CButton
                                                color="primary"
                                                class="px-4"
                                                type="submit"
                                            >
                                                Accept <CIcon v-if="!useSpinners || !isSubmitting" name="cilCheck" />
                                                         <CSpinner v-if="useSpinners && isSubmitting" color="info" size="sm" />
                                            </CButton>

                                        </div>
                                    </CCol>
                                </CRow>
                            </CForm>
                        </CCardBody>
                    </CCard>
                </CCardGroup>
            </CCol>
        </CRow>
    </CContainer>
</template>

<script>
/**
 * DropShiptEntry Vue.js view page for the Portal application for Suncast
 *
 * Provides a form for the user to enter a single occurance shipping address
 *
 * @author Kevin Jones (Fresche Solutions)
 * @date June 2020
 */
import { mapState, mapGetters, mapMutations, mapActions } from "vuex";
import { generalForm } from "../../../../services/validators";
import ShipDatePicker from "../components/ShipDatePicker";

const qs = require("qs");

const VALIDATION_MAX_LENGTHS = {
    name: 25,
    addressLine1: 50,
    addressLine2: 50,
    city: 25,
    postal: 12,
};

export default {
    name: "DropShipAddressEntry",
    components: {
        ShipDatePicker
    },
    data() {
        return {
            MAX_LENGTHS: VALIDATION_MAX_LENGTHS,
            redirectTo: '',
            userData: {},
            isSubmitting: false,
            customerHasWarehouses: false,

            // Fields
            name: '',
            city: '',
            postal: '',
            addressLine1: '',
            addressLine2: '',

            // State
            selectedState: null,
            stateOptions: [],

            // Country
            countrySelectDisabled: true,
            selectedCountry: null,
            countryOptions: [],

            // Estimated (requested) ship date
            estimatedShipDate: '',
        };
    },
    props: {
        // For inline navigation. Set this to true if displaying within another page
        inline: {
            required: false,
            type: [Boolean],
            default: false,
        },
        useSpinners: {
            required: false,
            type: [Boolean],
            default: true,
        },
    },
    computed: {

        // Is state selection valid?
        isStateValid() {
            return (this.selectedState != null && this.selectedState.value != '');
        },

        // State or Province label - switches depending on selected country
        labelStateOrProvince() {
            return (this.selectedCountry == 'CA' ? 'Province' : 'State');
        },
    },
    async mounted() {

        // Update the redirect from route, if provided. Otherwise user's home page.
        this.redirectTo = this.getUserHomepage();
        if (this.$route.params.redirect) {
            this.redirectTo = this.$route.params.redirect;
        }

        // If the selected country has been passed in route, set it. This will show the form.
        if (this.$route.params.selectedCountry) {
            this.selectedCountry = this.$route.params.selectedCountry;
        }

        // Initialize the page
        await this.init();
    },
    methods: {
        ...mapGetters("User", ["getSelectedCustomer", "getUserCustomers", "getCountryList", "getStateList", "getCustomerWarehouses", "getUserHomepage", "getUserData"]),
        ...mapActions("User", ["fetchUserData", "updateSessionData", "fetchUserCustomers", "fetchCountries", "fetchStates", "validateAddress", "fetchCustomerWarehouses"]),
        ...mapMutations("User", ["setUserData"]),
        ...mapActions("Product", ["fetchProductCategories"]),
        ...mapMutations("Cart", ["updateActiveCart"]),
        ...generalForm, // mapping functions from generalForm object in validators.js

        //
        // Initialize the page
        //
        async init() {

            // Load the countries select first - it's usually needed first and isn't dependant on userData fetched below
            this.loadCountrySelect();

            // Get the user data. This has the currently selected customer and drop-ship info, if any.
            this.userData = this.getUserData();
            if (!this.userData) {
                await this.fetchUserData();
                this.userData = this.getUserData();
            }

            // Get the current estimated ship date from userData as the default
            let estimatedShipDate = this.userData.currentEstimatedShipDate;
            if (typeof estimatedShipDate == 'string' && estimatedShipDate != '') {
                this.setShipDate(estimatedShipDate);
            }

            // Get the customer's warehouses, fetching them if necessary
            let customerWarehouses = this.getCustomerWarehouses();
            if (customerWarehouses.length == 0) {
                await this.fetchCustomerWarehouses();
                customerWarehouses = this.getCustomerWarehouses();
            }
            this.customerHasWarehouses = (customerWarehouses.length > 0);

            // If we have the selected country , update drop ship address from user data
            if (this.selectedCountry != null) {
                this.repopulateForm();
            }

            // Load the states/provinces, selecting if a country has been selected
            this.loadStatesSelect();
        },

        //
        // Set the selected Ship Date
        //
        setShipDate(date) {
            this.estimatedShipDate = date;
            this.userData.currentEstimatedShipDate = this.estimatedShipDate;
            this.setUserData(this.userData);
        },

        //
        // Load the countries & the select box options
        //
        async loadCountrySelect() {

            let countries = this.getCountryList();

            // Don't have them yet - fetch and try again
            if (countries == null || countries.length == 0) {
                await this.fetchCountries();
                countries = this.getCountryList();
            }

            // Load the countries into the select box & enable it
            this.countryOptions = countries;
            this.countrySelectDisabled = false;
        },

        //
        // Load the states/provinces & the select box options
        //
        async loadStatesSelect() {

            let statesAndProvinces = this.getStateList();

            // Don't have them yet - fetch and try again
            if (statesAndProvinces == null || statesAndProvinces.length == 0) {
                await this.fetchStates();
                statesAndProvinces = this.getStateList();
            }

            // Load the selected country's states/provinces into the select box if a country has been selected
            if (this.selectedCountry != null) {
                this.stateOptions = statesAndProvinces[this.selectedCountry];
            }
        },

        //
        // Populate the state/province select box
        //
        handleCountryChange() {

            // If the selected country is different from a previously selected country, clear the form
            if (this.userData.currentDropShipInfo == null || this.selectedCountry != this.userData.currentDropShipInfo.country) {
                this.name = '';
                this.setAddressValues({ state: null });     // All will be set to empty string but we want state to be null
            }

            // Populate the states/provinces select box
            this.loadStatesSelect();

            // Update drop ship address from user data, if there is any
            this.repopulateForm();
        },

        //
        // Set all address values except country which is selected manually
        //
        setAddressValues(addressObj) {
            this.city          = (addressObj.city         ? addressObj.city         : '');
            this.postal        = (addressObj.postal       ? addressObj.postal       : '');
            this.addressLine1  = (addressObj.addressLine1 ? addressObj.addressLine1 : '');
            this.addressLine2  = (addressObj.addressLine2 ? addressObj.addressLine2 : '');
            this.selectedState = (addressObj.state        ? addressObj.state        : '');
        },

        //
        // Populate the form data if we have it
        //
        repopulateForm() {

            // Nothing to do if we don't have previous drop-ship info or it's from a different country
            if (this.userData.currentDropShipInfo == null || this.selectedCountry != this.userData.currentDropShipInfo.country) {
                return;
            }

            // Update drop ship address from user data
            // Don't preselect the country, though - it needs to be manually selected on each form load.
            this.name = this.userData.currentDropShipInfo.name;
            this.setAddressValues(this.userData.currentDropShipInfo);
        },

        //
        // Submit the form
        //
        async doSubmit(e) {
            e.preventDefault();

            this.isSubmitting = true;

            // Validate the address and correct if necessary/possible
            let fullAddress = {
                addressLine1: this.addressLine1,
                addressLine2: this.addressLine2,
                city: this.city,
                postal: this.postal,
                state: this.selectedState,
                country: this.selectedCountry ? this.selectedCountry : '',
            };

            let response = await this.validateAddress(fullAddress);

            this.isSubmitting = false;
            if (!response.success) {
                for (let i = 0; i < response.errors.length; i++) {
                    this.$toast.open({
                        message: response.errors[i],
                        type: "error"
                    });
                }
                return;
            }

            // Update the form with what was returned before updating
            if (response.address) {
                this.city = (response.address.city ? response.address.city : '');
                this.postal = (response.address.postal ? response.address.postal : '');
                this.addressLine1 = (response.address.addressLine1 ? response.address.addressLine1 : '');
                this.addressLine2 = (response.address.addressLine2 ? response.address.addressLine2 : '');
                this.selectedState = (response.address.state ? response.address.state : '');
            }

            // Update the user's address & proceed to the next page
            this.updateAndRedirect(this.redirectTo);
        },

        //
        // Update session and redirect to the next page
        //
        async updateAndRedirect(redirectTo) {

            this.isSubmitting = true;

            // Get the selected customer
            let selectedUserCustomer = await this.getSelectedCustomer();
            if (selectedUserCustomer === undefined || Object.keys(selectedUserCustomer).length == 0) {

                // Get the user's customers, loading the list if necessary
                let userCustomers = this.getUserCustomers();
                if (Object.keys(userCustomers).length == 0) {
                    await this.fetchUserCustomers();
                    userCustomers = this.getUserCustomers();
                }

                // Get the selected customer's ID from user data
                let selectedCustomerId = this.userData.currentCustomer;

                // Get the selected customer from the user's customer list
                selectedUserCustomer = userCustomers.find((userCustomer, index) => userCustomer.customerId == selectedCustomerId);
            }

            let newDropShipInfo = {
                name: this.name,
                city: this.city,
                postal: this.postal,
                addressLine1: this.addressLine1,
                addressLine2: this.addressLine2,
                country: this.selectedCountry,
                state: this.selectedState,
            };

            // Update server-side session data with the selected customer, drop ship info, and clear the warehouse
            await this.updateSessionData({
                customerId: selectedUserCustomer.customerId,
                customer: selectedUserCustomer,
                warehouseId: null,
                dropShipInfo: newDropShipInfo,
                warehouse: {},                      // Clear a previous warehouse selection, if any
                clearWarehouse: true,               // Set a flag to clear all warehouse data
                estimatedShipDate: this.estimatedShipDate,
            });

            // Update the drop ship info in UserData
            this.updateUserData(newDropShipInfo);

            // update active cart with new tax data from userdata
            this.updateActiveCart(null);

            this.isSubmitting = false;

            // Emit the selection event
            this.$emit('selected', newDropShipInfo);

            // Redirect if displaying as its own page. If inline, the app will pick up the address and
            // change on its own.
            if (!this.inline) {
                if (redirectTo.indexOf('/') === -1) {
                    this.$router.push({ name: redirectTo });
                }
                // Redirect to requested path
                else {
                    this.$router.push(redirectTo);
                }
            }

            // Load the products for the current customer, shipping address, and date
            await this.fetchProductCategories(this.estimatedShipDate);
        },

        // Save to UserData the drop ship info and selected ship date, clearing the selected warehouse
        updateUserData(newDropShipInfo) {

            this.userData = this.getUserData();

            this.userData.currentWarehouse = '';
            this.userData.currentWarehouseAddress = {};
            this.userData.currentDropShipInfo = newDropShipInfo;
            this.userData.currentEstimatedShipDate = this.estimatedShipDate;

            this.setUserData(this.userData);
        },

        //
        // Go back to Customer selection
        //
        goToCustomerSelection(e) {
            e.preventDefault();
            this.$router.push({ name: "CustomerSelection" });
        },

        //
        // Go to the Warehouse Selection form
        //
        goToWarehouseSelection() {

            // Emit the navigate to Drop Ship event
            this.$emit('navigate', 'WarehouseSelection');

            if (!this.inline) {
                this.$router.push({ name: "WarehouseSelection" , params: { redirect: this.redirectTo } });
            }
        },
    }
};
</script>

<style scoped>
    .dropship-entry.in-page {
        align-items: unset !important;
    }
</style>
